/**
 * SmartGeoResolver
 * 
 * Multi-source geolocation resolver that combines:
 * 1. HTTP headers (CDN PoP codes) - highest confidence
 * 2. Reverse DNS hostname parsing - medium confidence
 * 3. IP geolocation (ip-api/MaxMind) - baseline
 * 4. RTT validation to detect impossible locations
 * 
 * Works in both Electron (full capabilities) and webapp (limited by CORS).
 */

import type { GeoLookupResult, GeoConfidence, GeoSource } from './IGeoProvider';
import { DEFAULT_GEO_RESULT } from './IGeoProvider';
import { getGeoProvider } from './GeoProviderFactory';
import { GeoLocationValidator } from './GeoLocationValidator';
import { createLogger } from '../../utils/logger';
const log = createLogger('SmartGeoResolver');

/**
 * Options for enhanced geolocation resolution
 */
export interface SmartGeoOptions {
    rttMs?: number;
    userLat?: number;
    userLon?: number;
    httpHeaders?: Record<string, string>;
    hostname?: string;
}

/**
 * Known CDN location codes → coordinates
 * Used for header-based and DNS-based location resolution
 */
const CDN_LOCATIONS: Record<string, { lat: number; lon: number; city: string; country: string }> = {
    // Belgium
    'bru': { lat: 50.8503, lon: 4.3517, city: 'Brussels', country: 'Belgium' },
    'brussels': { lat: 50.8503, lon: 4.3517, city: 'Brussels', country: 'Belgium' },
    'ant': { lat: 51.2194, lon: 4.4025, city: 'Antwerp', country: 'Belgium' },
    'antwerp': { lat: 51.2194, lon: 4.4025, city: 'Antwerp', country: 'Belgium' },
    // Netherlands
    'ams': { lat: 52.3676, lon: 4.9041, city: 'Amsterdam', country: 'Netherlands' },
    'amsterdam': { lat: 52.3676, lon: 4.9041, city: 'Amsterdam', country: 'Netherlands' },
    // Germany
    'fra': { lat: 50.1109, lon: 8.6821, city: 'Frankfurt', country: 'Germany' },
    'frankfurt': { lat: 50.1109, lon: 8.6821, city: 'Frankfurt', country: 'Germany' },
    'ber': { lat: 52.5200, lon: 13.4050, city: 'Berlin', country: 'Germany' },
    'berlin': { lat: 52.5200, lon: 13.4050, city: 'Berlin', country: 'Germany' },
    'dus': { lat: 51.2277, lon: 6.7735, city: 'Dusseldorf', country: 'Germany' },
    'dusseldorf': { lat: 51.2277, lon: 6.7735, city: 'Dusseldorf', country: 'Germany' },
    'ham': { lat: 53.5511, lon: 9.9937, city: 'Hamburg', country: 'Germany' },
    'hamburg': { lat: 53.5511, lon: 9.9937, city: 'Hamburg', country: 'Germany' },
    'muc': { lat: 48.1351, lon: 11.5820, city: 'Munich', country: 'Germany' },
    'munich': { lat: 48.1351, lon: 11.5820, city: 'Munich', country: 'Germany' },
    // France
    'cdg': { lat: 48.8566, lon: 2.3522, city: 'Paris', country: 'France' },
    'par': { lat: 48.8566, lon: 2.3522, city: 'Paris', country: 'France' },
    'paris': { lat: 48.8566, lon: 2.3522, city: 'Paris', country: 'France' },
    'mrs': { lat: 43.2965, lon: 5.3698, city: 'Marseille', country: 'France' },
    'marseille': { lat: 43.2965, lon: 5.3698, city: 'Marseille', country: 'France' },
    // UK
    'lhr': { lat: 51.5074, lon: -0.1278, city: 'London', country: 'UK' },
    'lon': { lat: 51.5074, lon: -0.1278, city: 'London', country: 'UK' },
    'london': { lat: 51.5074, lon: -0.1278, city: 'London', country: 'UK' },
    'man': { lat: 53.4808, lon: -2.2426, city: 'Manchester', country: 'UK' },
    'manchester': { lat: 53.4808, lon: -2.2426, city: 'Manchester', country: 'UK' },
    // Ireland
    'dub': { lat: 53.3498, lon: -6.2603, city: 'Dublin', country: 'Ireland' },
    'dublin': { lat: 53.3498, lon: -6.2603, city: 'Dublin', country: 'Ireland' },
    // Spain
    'mad': { lat: 40.4168, lon: -3.7038, city: 'Madrid', country: 'Spain' },
    'madrid': { lat: 40.4168, lon: -3.7038, city: 'Madrid', country: 'Spain' },
    'bcn': { lat: 41.3851, lon: 2.1734, city: 'Barcelona', country: 'Spain' },
    'barcelona': { lat: 41.3851, lon: 2.1734, city: 'Barcelona', country: 'Spain' },
    // Italy
    'mxp': { lat: 45.4642, lon: 9.1900, city: 'Milan', country: 'Italy' },
    'mil': { lat: 45.4642, lon: 9.1900, city: 'Milan', country: 'Italy' },
    'milan': { lat: 45.4642, lon: 9.1900, city: 'Milan', country: 'Italy' },
    'fco': { lat: 41.9028, lon: 12.4964, city: 'Rome', country: 'Italy' },
    'rome': { lat: 41.9028, lon: 12.4964, city: 'Rome', country: 'Italy' },
    // Scandinavia
    'arn': { lat: 59.3293, lon: 18.0686, city: 'Stockholm', country: 'Sweden' },
    'sto': { lat: 59.3293, lon: 18.0686, city: 'Stockholm', country: 'Sweden' },
    'stockholm': { lat: 59.3293, lon: 18.0686, city: 'Stockholm', country: 'Sweden' },
    'cph': { lat: 55.6761, lon: 12.5683, city: 'Copenhagen', country: 'Denmark' },
    'copenhagen': { lat: 55.6761, lon: 12.5683, city: 'Copenhagen', country: 'Denmark' },
    'osl': { lat: 59.9139, lon: 10.7522, city: 'Oslo', country: 'Norway' },
    'oslo': { lat: 59.9139, lon: 10.7522, city: 'Oslo', country: 'Norway' },
    'hel': { lat: 60.1699, lon: 24.9384, city: 'Helsinki', country: 'Finland' },
    'helsinki': { lat: 60.1699, lon: 24.9384, city: 'Helsinki', country: 'Finland' },
    // Eastern Europe
    'waw': { lat: 52.2297, lon: 21.0122, city: 'Warsaw', country: 'Poland' },
    'warsaw': { lat: 52.2297, lon: 21.0122, city: 'Warsaw', country: 'Poland' },
    'vie': { lat: 48.2082, lon: 16.3738, city: 'Vienna', country: 'Austria' },
    'vienna': { lat: 48.2082, lon: 16.3738, city: 'Vienna', country: 'Austria' },
    'prg': { lat: 50.0755, lon: 14.4378, city: 'Prague', country: 'Czech Republic' },
    'prague': { lat: 50.0755, lon: 14.4378, city: 'Prague', country: 'Czech Republic' },
    // Switzerland
    'zrh': { lat: 47.3769, lon: 8.5417, city: 'Zurich', country: 'Switzerland' },
    'zur': { lat: 47.3769, lon: 8.5417, city: 'Zurich', country: 'Switzerland' },
    'zurich': { lat: 47.3769, lon: 8.5417, city: 'Zurich', country: 'Switzerland' },
    'gva': { lat: 46.2044, lon: 6.1432, city: 'Geneva', country: 'Switzerland' },
    'geneva': { lat: 46.2044, lon: 6.1432, city: 'Geneva', country: 'Switzerland' },
    // US East
    'iad': { lat: 39.0438, lon: -77.4874, city: 'Ashburn', country: 'USA' },
    'ashburn': { lat: 39.0438, lon: -77.4874, city: 'Ashburn', country: 'USA' },
    'ewr': { lat: 40.7128, lon: -74.0060, city: 'New York', country: 'USA' },
    'jfk': { lat: 40.7128, lon: -74.0060, city: 'New York', country: 'USA' },
    'nyc': { lat: 40.7128, lon: -74.0060, city: 'New York', country: 'USA' },
    'new york': { lat: 40.7128, lon: -74.0060, city: 'New York', country: 'USA' },
    'bos': { lat: 42.3601, lon: -71.0589, city: 'Boston', country: 'USA' },
    'boston': { lat: 42.3601, lon: -71.0589, city: 'Boston', country: 'USA' },
    'mia': { lat: 25.7617, lon: -80.1918, city: 'Miami', country: 'USA' },
    'miami': { lat: 25.7617, lon: -80.1918, city: 'Miami', country: 'USA' },
    'atl': { lat: 33.7490, lon: -84.3880, city: 'Atlanta', country: 'USA' },
    'atlanta': { lat: 33.7490, lon: -84.3880, city: 'Atlanta', country: 'USA' },
    // US Central
    'ord': { lat: 41.8781, lon: -87.6298, city: 'Chicago', country: 'USA' },
    'chi': { lat: 41.8781, lon: -87.6298, city: 'Chicago', country: 'USA' },
    'chicago': { lat: 41.8781, lon: -87.6298, city: 'Chicago', country: 'USA' },
    'dfw': { lat: 32.7767, lon: -96.7970, city: 'Dallas', country: 'USA' },
    'dallas': { lat: 32.7767, lon: -96.7970, city: 'Dallas', country: 'USA' },
    'den': { lat: 39.7392, lon: -104.9903, city: 'Denver', country: 'USA' },
    'denver': { lat: 39.7392, lon: -104.9903, city: 'Denver', country: 'USA' },
    // US West
    'lax': { lat: 34.0522, lon: -118.2437, city: 'Los Angeles', country: 'USA' },
    'los angeles': { lat: 34.0522, lon: -118.2437, city: 'Los Angeles', country: 'USA' },
    'sfo': { lat: 37.7749, lon: -122.4194, city: 'San Francisco', country: 'USA' },
    'san francisco': { lat: 37.7749, lon: -122.4194, city: 'San Francisco', country: 'USA' },
    'sjc': { lat: 37.3382, lon: -121.8863, city: 'San Jose', country: 'USA' },
    'san jose': { lat: 37.3382, lon: -121.8863, city: 'San Jose', country: 'USA' },
    'sea': { lat: 47.6062, lon: -122.3321, city: 'Seattle', country: 'USA' },
    'seattle': { lat: 47.6062, lon: -122.3321, city: 'Seattle', country: 'USA' },
    'phx': { lat: 33.4484, lon: -112.0740, city: 'Phoenix', country: 'USA' },
    'phoenix': { lat: 33.4484, lon: -112.0740, city: 'Phoenix', country: 'USA' },
    // Canada
    'yyz': { lat: 43.6532, lon: -79.3832, city: 'Toronto', country: 'Canada' },
    'tor': { lat: 43.6532, lon: -79.3832, city: 'Toronto', country: 'Canada' },
    'toronto': { lat: 43.6532, lon: -79.3832, city: 'Toronto', country: 'Canada' },
    'yul': { lat: 45.5017, lon: -73.5673, city: 'Montreal', country: 'Canada' },
    'mtl': { lat: 45.5017, lon: -73.5673, city: 'Montreal', country: 'Canada' },
    'montreal': { lat: 45.5017, lon: -73.5673, city: 'Montreal', country: 'Canada' },
    'yvr': { lat: 49.2827, lon: -123.1207, city: 'Vancouver', country: 'Canada' },
    'vancouver': { lat: 49.2827, lon: -123.1207, city: 'Vancouver', country: 'Canada' },
    // Asia Pacific
    'nrt': { lat: 35.6762, lon: 139.6503, city: 'Tokyo', country: 'Japan' },
    'hnd': { lat: 35.6762, lon: 139.6503, city: 'Tokyo', country: 'Japan' },
    'tyo': { lat: 35.6762, lon: 139.6503, city: 'Tokyo', country: 'Japan' },
    'tokyo': { lat: 35.6762, lon: 139.6503, city: 'Tokyo', country: 'Japan' },
    'sin': { lat: 1.3521, lon: 103.8198, city: 'Singapore', country: 'Singapore' },
    'singapore': { lat: 1.3521, lon: 103.8198, city: 'Singapore', country: 'Singapore' },
    'hkg': { lat: 22.3193, lon: 114.1694, city: 'Hong Kong', country: 'Hong Kong' },
    'hong kong': { lat: 22.3193, lon: 114.1694, city: 'Hong Kong', country: 'Hong Kong' },
    'syd': { lat: -33.8688, lon: 151.2093, city: 'Sydney', country: 'Australia' },
    'sydney': { lat: -33.8688, lon: 151.2093, city: 'Sydney', country: 'Australia' },
    'icn': { lat: 37.5665, lon: 126.9780, city: 'Seoul', country: 'South Korea' },
    'sel': { lat: 37.5665, lon: 126.9780, city: 'Seoul', country: 'South Korea' },
    'seoul': { lat: 37.5665, lon: 126.9780, city: 'Seoul', country: 'South Korea' },
    'bom': { lat: 19.0760, lon: 72.8777, city: 'Mumbai', country: 'India' },
    'mumbai': { lat: 19.0760, lon: 72.8777, city: 'Mumbai', country: 'India' },
};

export class SmartGeoResolver {
    /**
     * Resolve location for an IP address using multiple sources
     * Falls back through sources in order of reliability
     */
    public static async resolveLocation(
        ip: string,
        options: SmartGeoOptions = {}
    ): Promise<GeoLookupResult> {
        // 1. Try HTTP headers first (highest confidence - CDN tells us directly)
        if (options.httpHeaders) {
            const headerResult = this.parseLocationFromHeaders(options.httpHeaders, ip);
            if (headerResult && headerResult.city) {
                log.log(`✅ MATCH (Source: Headers): ${headerResult.city} (Code: ${headerResult.city.toUpperCase().substring(0, 3)})`);
                return headerResult;
            }
        }

        // 2. Try reverse DNS hostname parsing
        if (options.hostname) {
            const dnsResult = this.parseLocationFromHostname(options.hostname, ip);
            if (dnsResult && dnsResult.city) {
                log.log(`✅ MATCH (Source: DNS): ${dnsResult.city} (Hostname: ${options.hostname})`);
                return dnsResult;
            }
        }

        // 3. Fall back to IP geolocation provider
        const provider = getGeoProvider();
        let geoResult: GeoLookupResult;

        try {
            geoResult = await provider.lookupIp(ip);
            geoResult.source = provider.name.toLowerCase().includes('maxmind') ? 'maxmind' : 'ip-api';
            if (geoResult.city) {
                log.log(`✅ MATCH (Source: IP/${geoResult.source}): ${geoResult.city}`);
            }
        } catch (error) {
            log.warn(`IP lookup failed for ${ip}:`, error);
            return { ...DEFAULT_GEO_RESULT, ip, confidence: 'low', source: 'fallback' };
        }

        // 4. Validate with RTT if available
        if (options.rttMs && options.userLat !== undefined && options.userLon !== undefined) {
            const validation = GeoLocationValidator.validate(
                options.userLat,
                options.userLon,
                geoResult.latitude,
                geoResult.longitude,
                options.rttMs
            );

            if (validation.result === 'impossible' || validation.result === 'unlikely') {
                log.log(
                    `[SmartGeoResolver] ⚠️ RTT MISMATCH: ${validation.result} for ${ip} ` +
                    `(${validation.distanceKm.toFixed(0)}km, RTT: ${validation.measuredRttMs}ms, ` +
                    `min theoretical: ${validation.theoreticalMinRttMs.toFixed(0)}ms)`
                );

                // PROACTIVE CORRECTION:
                // If IP-based location is impossible but we have a hostname, 
                // try harder to parse location from hostname as it might be an anycast node
                if (options.hostname) {
                    const retryDns = this.parseLocationFromHostname(options.hostname, ip);
                    if (retryDns && retryDns.city) {
                        const retryValidation = GeoLocationValidator.validate(
                            options.userLat,
                            options.userLon,
                            retryDns.latitude,
                            retryDns.longitude,
                            options.rttMs
                        );

                        if (retryValidation.result === 'valid') {
                            log.log(`✅ RECTIFIED: Hostname match "${retryDns.city}" is plausible for RTT.`);
                            return { ...retryDns, confidence: 'medium' };
                        }
                    }
                }

                // If still impossible/unlikely and mismatch is severe (>1000km gap),
                // mark as low confidence and clear city/coords to trigger fallback or filtering
                geoResult.confidence = 'low';
                const isContinentJump = validation.distanceKm > 2000 && validation.measuredRttMs < 50;
                const isVeryInaccurate = validation.result === 'impossible' ||
                    (validation.result === 'unlikely' && validation.distanceKm > 1000) ||
                    isContinentJump;

                if (isVeryInaccurate) {
                    log.log(`❌ INVALIDATING: Location "${geoResult.city}" is physically ${validation.result}${isContinentJump ? ' (Continent Jump Detected)' : ''}. Clearing coords.`);
                    geoResult.city = null;
                    geoResult.latitude = 0;
                    geoResult.longitude = 0;
                }
            } else {
                log.log(`🟢 RTT VALID: Matches distance (${validation.distanceKm.toFixed(0)}km)`);
                geoResult.confidence = 'medium';
            }
        } else {
            geoResult.confidence = geoResult.city ? 'medium' : 'low';
        }

        return geoResult;
    }

    /**
     * Parse location from HTTP headers (CDN-specific)
     */
    private static parseLocationFromHeaders(
        headers: Record<string, string>,
        ip: string
    ): GeoLookupResult | null {
        // Normalize header keys to lowercase
        const normalizedHeaders: Record<string, string> = {};
        for (const [key, value] of Object.entries(headers)) {
            normalizedHeaders[key.toLowerCase()] = value;
        }

        // CloudFront: x-amz-cf-pop (e.g., "AMS1-C1", "FRA50-P1")
        const cfPop = normalizedHeaders['x-amz-cf-pop'];
        if (cfPop) {
            const locationCode = cfPop.substring(0, 3).toLowerCase();
            const location = CDN_LOCATIONS[locationCode];
            if (location) {
                return this.buildResult(ip, location, 'high', 'header', 'AWS CloudFront', null);
            }
        }

        // Fastly: x-served-by (e.g., "cache-ams21041-AMS")
        const servedBy = normalizedHeaders['x-served-by'];
        if (servedBy) {
            const match = servedBy.match(/cache-([a-z]{3})\d+-([A-Z]{3})/i);
            if (match) {
                const locationCode = match[2].toLowerCase();
                const location = CDN_LOCATIONS[locationCode];
                if (location) {
                    return this.buildResult(ip, location, 'high', 'header', 'Fastly', servedBy);
                }
            }
        }

        // Cloudflare: cf-ray (e.g., "xxxxx-AMS")
        const cfRay = normalizedHeaders['cf-ray'];
        if (cfRay) {
            const match = cfRay.match(/-([A-Z]{3})$/i);
            if (match) {
                const locationCode = match[1].toLowerCase();
                const location = CDN_LOCATIONS[locationCode];
                if (location) {
                    return this.buildResult(ip, location, 'high', 'header', 'Cloudflare', null);
                }
            }
        }

        // Akamai: x-akamai-request-id may contain region info
        // (Less reliable, but can try)

        return null;
    }

    /**
     * Parse location from hostname (reverse DNS)
     */
    private static parseLocationFromHostname(
        hostname: string,
        ip: string
    ): GeoLookupResult | null {
        const lowerHostname = hostname.toLowerCase();

        // Fastly: cache-ams21041-AMS.fastly.net
        const fastlyMatch = lowerHostname.match(/cache-([a-z]{3})\d+-([a-z]{3})/);
        if (fastlyMatch) {
            const locationCode = fastlyMatch[2];
            const location = CDN_LOCATIONS[locationCode];
            if (location) {
                return this.buildResult(ip, location, 'medium', 'dns', 'Fastly', hostname);
            }
        }

        // CloudFront: server-52-84-125-47.ams50.r.cloudfront.net
        const cloudfrontMatch = lowerHostname.match(/\.([a-z]{3})\d+\.r\.cloudfront\.net/);
        if (cloudfrontMatch) {
            const locationCode = cloudfrontMatch[1];
            const location = CDN_LOCATIONS[locationCode];
            if (location) {
                return this.buildResult(ip, location, 'medium', 'dns', 'AWS CloudFront', hostname);
            }
        }

        // Akamai: detect from hostname patterns
        if (lowerHostname.includes('akamai')) {
            // Try to find location code in Akamai hostname
            for (const [code, location] of Object.entries(CDN_LOCATIONS)) {
                if (code.length === 3 && lowerHostname.includes(code)) {
                    const pattern = new RegExp(`[^a-z]${code}[^a-z]|^${code}[^a-z]|[^a-z]${code}$`);
                    if (pattern.test(lowerHostname)) {
                        return this.buildResult(ip, location, 'medium', 'dns', 'Akamai', hostname);
                    }
                }
            }
            // If no location code found but it's Akamai, we still know the provider
            // Use ip-api or fallback for location, but let's at least mark it Akamai
        }

        // Generic: look for known location codes in hostname
        for (const [code, location] of Object.entries(CDN_LOCATIONS)) {
            if (code.length === 3 && lowerHostname.includes(code)) {
                // Only match if it looks like a location code (surrounded by non-alpha)
                const pattern = new RegExp(`[^a-z]${code}[^a-z]|^${code}[^a-z]|[^a-z]${code}$`);
                if (pattern.test(lowerHostname)) {
                    return this.buildResult(ip, location, 'medium', 'dns', null, hostname);
                }
            }
        }

        return null;
    }

    /**
     * Build a GeoLookupResult from location data
     */
    private static buildResult(
        ip: string,
        location: { lat: number; lon: number; city: string; country: string },
        confidence: GeoConfidence,
        source: GeoSource,
        org: string | null = null,
        hostname: string | null = null
    ): GeoLookupResult {
        return {
            ip,
            city: location.city,
            country: location.country,
            countryCode: null,
            latitude: location.lat,
            longitude: location.lon,
            org: org,
            isp: org,
            timezone: null,
            hostname: hostname,
            confidence,
            source
        };
    }

    /**
     * Lookup location code from CDN_LOCATIONS
     * Useful for external callers that need to resolve a code to coordinates
     */
    public static lookupLocationCode(code: string): { lat: number; lon: number; city: string; country: string } | null {
        return CDN_LOCATIONS[code.toLowerCase()] || null;
    }
}
